/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useState,
} from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import {
  Button,
  TextArea,
  Input,
  Modal,
  Price,
  Table,
} from '@adobe-commerce/elsie/components';
import { CartItemModel } from '@/quote-management/data/models/negotiable-quote-model';
import '@/quote-management/components/LineItemNoteModal/LineItemNoteModal.css';

export interface LineItemNoteModalProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  open: boolean;
  item: CartItemModel;
  onClose?: () => void;
  onConfirm: (note: string, quantity: number) => void;
  isSubmitting?: boolean;
  errorBanner?: VNode;
  successBanner?: VNode;
  showCloseButton?: boolean;
  readOnlyQuantity?: boolean;
}

export const LineItemNoteModal: FunctionComponent<LineItemNoteModalProps> = ({
  className,
  open,
  item,
  onClose,
  onConfirm,
  isSubmitting = false,
  errorBanner,
  successBanner,
  showCloseButton = true,
  readOnlyQuantity = false,
}) => {
  const [note, setNote] = useState('');
  const [quantity, setQuantity] = useState(item.quantity);
  const [errors, setErrors] = useState<{ note?: string; quantity?: string }>(
    {}
  );

  const dictionary = useText({
    title: 'NegotiableQuote.Manage.lineItemNote.title',
    productLabel: 'NegotiableQuote.Manage.lineItemNote.productLabel',
    skuLabel: 'NegotiableQuote.Manage.lineItemNote.skuLabel',
    priceLabel: 'NegotiableQuote.Manage.lineItemNote.priceLabel',
    stockLabel: 'NegotiableQuote.Manage.lineItemNote.stockLabel',
    quantityLabel: 'NegotiableQuote.Manage.lineItemNote.quantityLabel',
    discountLabel: 'NegotiableQuote.Manage.lineItemNote.discountLabel',
    subtotalLabel: 'NegotiableQuote.Manage.lineItemNote.subtotalLabel',
    noteLabel: 'NegotiableQuote.Manage.lineItemNote.noteLabel',
    notePlaceholder: 'NegotiableQuote.Manage.lineItemNote.notePlaceholder',
    noteHelper: 'NegotiableQuote.Manage.lineItemNote.noteHelper',
    confirmButton: 'NegotiableQuote.Manage.lineItemNote.confirmButton',
    cancelButton: 'NegotiableQuote.Manage.lineItemNote.cancelButton',
    noteError: 'NegotiableQuote.Manage.lineItemNote.noteError',
    quantityError: 'NegotiableQuote.Manage.lineItemNote.quantityError',
  });

  // Reset form when modal opens with new item
  useEffect(() => {
    if (open) {
      // Get the most recent valid note from buyer (if exists)
      const validNotes =
        item.noteFromBuyer?.filter((note) => note && note.note) || [];
      const existingNote = validNotes.length > 0 ? validNotes[0].note : '';
      setNote(existingNote || '');
      setQuantity(item.quantity);
      setErrors({});
    }
  }, [open, item.quantity, item.noteFromBuyer]);

  const handleConfirm = useCallback(() => {
    const newErrors: { note?: string; quantity?: string } = {};

    // Check if the note field is empty
    if (!note.trim()) {
      newErrors.note = dictionary.noteError;
    }

    // Only validate quantity if it's editable
    if (!readOnlyQuantity && quantity <= 0) {
      newErrors.quantity = dictionary.quantityError;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onConfirm(note.trim(), quantity);
  }, [note, quantity, onConfirm, dictionary, readOnlyQuantity]);

  const handleCancel = useCallback(() => {
    setNote('');
    setQuantity(item.quantity);
    setErrors({});
    onClose?.();
  }, [onClose, item.quantity]);

  if (!open) {
    return null;
  }

  // Calculate discount display
  const hasDiscount = item.discounts && item.discounts.length > 0;
  const discountDisplay = hasDiscount
    ? item.discounts.map((d) => d.label).join(', ')
    : '-';

  // Define table columns
  const columns = [
    {
      label: dictionary.productLabel,
      key: 'productName',
    },
    {
      label: dictionary.priceLabel,
      key: 'price',
    },
    {
      label: dictionary.stockLabel,
      key: 'stock',
    },
    {
      label: dictionary.quantityLabel,
      key: 'quantity',
    },
    {
      label: dictionary.discountLabel,
      key: 'discount',
    },
    {
      label: dictionary.subtotalLabel,
      key: 'subtotal',
    },
  ];

  // Define table row data
  const rowData = [
    {
      productName: (
        <div className="quote-management-line-item-note-modal__product-info">
          <div className="quote-management-line-item-note-modal__product-name">
            {item.product.name}
          </div>
          <div className="quote-management-line-item-note-modal__product-sku">
            {dictionary.skuLabel}: {item.product.sku}
          </div>
        </div>
      ),
      price: (
        <Price
          amount={item.prices.originalItemPrice.value}
          currency={item.prices.originalItemPrice.currency}
        />
      ),
      stock: (
        <span className="quote-management-line-item-note-modal__stock">
          {item.stockStatus}
        </span>
      ),
      quantity: readOnlyQuantity ? (
        <span className="quote-management-line-item-note-modal__quantity-readonly">
          {item.quantity}
        </span>
      ) : (
        <Input
          name="quantity"
          type="number"
          min="1"
          value={quantity.toString()}
          onInput={(e: any) => {
            const newQuantity = parseInt(e.target.value, 10) || 0;
            setQuantity(newQuantity);
            setErrors({ ...errors, quantity: undefined });
          }}
          disabled={isSubmitting}
          error={!!errors.quantity}
          required
          data-testid="line-item-note-quantity-input"
          className="quote-management-line-item-note-modal__quantity-input"
        />
      ),
      discount: (
        <span className="quote-management-line-item-note-modal__discount">
          {discountDisplay}
        </span>
      ),
      subtotal: (
        <Price
          amount={item.prices.rowTotal.value}
          currency={item.prices.rowTotal.currency}
        />
      ),
    },
  ];

  return (
    <Modal
      open={open}
      size="medium"
      title={<>{dictionary.title}</>}
      onClose={handleCancel}
      clickToDismiss={true}
      escapeToDismiss={true}
      showCloseButton={showCloseButton}
      className={classes(['quote-management-line-item-note-modal', className])}
      data-testid="line-item-note-modal"
    >
      {errorBanner && (
        <div
          className="quote-management-line-item-note-modal__error-banner"
          data-testid="line-item-note-modal-error-banner"
        >
          {errorBanner}
        </div>
      )}

      {successBanner && (
        <div
          className="quote-management-line-item-note-modal__success-banner"
          data-testid="line-item-note-modal-success-banner"
        >
          {successBanner}
        </div>
      )}

      <div className="quote-management-line-item-note-modal__content">
        {/* Product Details Table */}
        <div className="quote-management-line-item-note-modal__details">
          <Table
            columns={columns}
            rowData={rowData}
            data-testid="line-item-note-table"
            mobileLayout="stacked"
            className="quote-management-line-item-note-modal__details-table"
          />
          {errors.quantity && (
            <div className="quote-management-line-item-note-modal__table-error">
              {errors.quantity}
            </div>
          )}
        </div>

        {/* Note TextArea */}
        <div className="quote-management-line-item-note-modal__form-field">
          <TextArea
            name="note"
            placeholder={dictionary.notePlaceholder}
            rows={4}
            value={note}
            onInput={(e: any) => {
              setNote(e.target.value);
              setErrors({ ...errors, note: undefined });
            }}
            label={dictionary.noteLabel}
            disabled={isSubmitting}
            data-testid="line-item-note-textarea"
          />
          {!errors.note && (
            <span className="quote-management-line-item-note-modal__helper-text">
              {dictionary.noteHelper}
            </span>
          )}
          {errors.note && (
            <span className="quote-management-line-item-note-modal__error-text">
              {errors.note}
            </span>
          )}
        </div>
      </div>

      <div className="quote-management-line-item-note-modal__actions">
        <Button
          variant="secondary"
          size="medium"
          onClick={handleCancel}
          disabled={isSubmitting}
          className="quote-management-line-item-note-modal__cancel-button"
          data-testid="line-item-note-cancel-button"
        >
          {dictionary.cancelButton}
        </Button>
        <Button
          variant="primary"
          size="medium"
          onClick={handleConfirm}
          disabled={isSubmitting}
          className="quote-management-line-item-note-modal__confirm-button"
          data-testid="line-item-note-confirm-button"
        >
          {dictionary.confirmButton}
        </Button>
      </div>
    </Modal>
  );
};
